// deps
import React, { ReactElement, memo, useContext, useMemo, useCallback } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { useEffectOnce } from "@swiggy-private/core-ui";

// components
import {
    CART_WIDGET_TYPES,
    CartGroupContainer,
    MenuItemsCallout,
    VALID_RENDERING_DETAILS_TYPE,
} from "@swiggy-private/cart-ui";
import { CartItemRenderer } from "@cart-client/components/cart-item-renderer";
import { ExtraItemList } from "@cart-client/components/extra-item-list";
import CartItemActions from "@cart-client/components/cart-items-actions/CartItemActions";
import { CrossSellM2ItemRenderer } from "@cart-client/components/cross-sell-m2-item-renderer";
import { OneMemberShipNudgeWrapper } from "@cart-client/components/one-membership-nudge-wrapper";
import { FreebieWidgetRenderer } from "@cart-client/components/freebie-widget-renderer";
import { StealDealWidgetRenderer } from "@cart-client/components/steal-deal-widget-renderer";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCrossSellAnalytics } from "@cart-client/hooks/useCrossSellAnalytics";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { useMenuCalloutAnalytics } from "@cart-client/hooks/useMenuCalloutAnalytics";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";

// helpers
import { getIrctcIdentifier, getMenuCalloutInfo } from "@cart-client/helpers/Cart";
import { isBulkOrder } from "@cart-client/helpers/BulkOrder";
import { AnalyticsContext, isPopFlow, RenderingDetails, useSessionStorage } from "@swiggy-private/checkout-core";
import { filterRenderingDetails } from "@cart-client/helpers/CartRendering";
import { getMenuItems, isCartContainBoltItems } from "@cart-client/helpers/Cart";

// analytics
import { impressionCartWidgetRanking } from "@cart-client/analytics/events";

//constans
import { DEFAULT_WIDGET_SECTIONS } from "./constants";
import { IS_IRCTC_CART_SESSION_KEY } from "@cart-client/constants/StorageKeys";

// styles
import { CartGroupContainerWrapper } from "./styles";

//types and inerfaces
import {
    ItemImagesOnCartXPVariants,
    FreebieWidgetRenderingDetails,
    StealDealWidgetRenderingDetails,
} from "@swiggy-private/cart-types";
import { CartMenuItemsAndSuggestionsProps } from "@cart-client/interfaces/CartMenuItemsAndSuggestions";
import { getFreebieWidgetData, shouldShowFreebieWidget } from "@cart-client/helpers/FreebieWidget";
import { getStealDealWidgetData, shouldShowStealDealWidget } from "@cart-client/helpers/StealDealWidget";

function CartMenuItemsAndSuggestions({
    isValueMeal,
    isCroutonVisible,
    renderingDetails,
    goToMenu,
}: CartMenuItemsAndSuggestionsProps): ReactElement | null {
    const { cartState } = useContext(CartContext);

    const { reviewedCart: cartResponse, localCart } = cartState;

    const { impressionEvent } = useContext(AnalyticsContext);

    const isCartBoltEnabled = isCartContainBoltItems(cartResponse?.data);

    const { onImpressionCrossSellM2Item } = useCrossSellAnalytics(cartResponse);

    const {
        onImpressionAddItemCarousel,
        onClickAddItemCarousel,
        onClickItemDetailsCarousel,
        onImpressionCarouselItemDetailsPopup,
        onImpressionCartItemDetails,
        onImpressionMagicItemsUnlockPopup,
        onClickDismissMagicItemsUnlockPopup,
        onClickExploreMenuMagicItems,
    } = useCartRendererAnalytics(cartResponse);

    const menuItemsCalloutAnalytics = useMenuCalloutAnalytics(cartResponse);

    const { itemImagesXP } = useCartRendererXP(cartResponse);

    const isBulkOrderFn = useMemo(() => {
        return isBulkOrder(cartResponse);
    }, [cartResponse]);

    const itemImageOnCartXpFlagExcludingBulkOrder = useMemo(() => {
        return isBulkOrderFn ? ItemImagesOnCartXPVariants.DEFAULT : itemImagesXP;
    }, [isBulkOrderFn, itemImagesXP]);

    const menuCallout = getMenuCalloutInfo(cartResponse);

    const itemListHeader = useMemo(
        () =>
            // remove this while deprecating coupon_selective_discounting_card
            cartResponse?.data?.coupon_details?.coupon_selective_discounting_card ??
            cartResponse?.data?.offerPresentationInfo?.cartMessageCard,
        [cartResponse],
    );

    const isPopCart = isPopFlow();

    const disableRedirectionToMenu = isValueMeal;

    const [isIrctcCartSession] = useSessionStorage(IS_IRCTC_CART_SESSION_KEY, !!getIrctcIdentifier(cartResponse));

    const widgetSections = useMemo(() => {
        const widgetSections = filterRenderingDetails(renderingDetails, CART_WIDGET_TYPES);
        return !isEmpty(widgetSections) ? widgetSections : DEFAULT_WIDGET_SECTIONS;
    }, [renderingDetails]);

    const menuItemsWidget = useCallback(() => {
        if (isEmpty(cartResponse)) {
            return null;
        }

        return (
            <CartGroupContainerWrapper>
                <CartGroupContainer>
                    <OneMemberShipNudgeWrapper />
                    <MenuItemsCallout calloutInfo={menuCallout?.info} analyticsPayload={menuItemsCalloutAnalytics} />
                    <CartItemRenderer
                        cartItems={getMenuItems(cartResponse)}
                        groupTagDetails={cartResponse?.data?.group_tag_details}
                        mealItems={[]}
                        restaurantDetails={cartResponse?.data?.restaurant_details}
                        couponDetails={cartResponse?.data?.coupon_details}
                        cartMenuItemsHeaderList={cartResponse?.data?.cart_menu_items_header_list}
                        itemListHeader={itemListHeader}
                        showInputQtyField={isBulkOrderFn}
                        isValueMeal={isValueMeal}
                        itemImagesXP={itemImageOnCartXpFlagExcludingBulkOrder}
                        isIrctcCartSession={isIrctcCartSession}
                    />
                    <ExtraItemList cartResponse={cartResponse} />
                    <CartItemActions
                        disableRedirectionToMenu={disableRedirectionToMenu}
                        specialInstruction={localCart?.orderComment ?? ""}
                        isCroutonVisible={isCroutonVisible}
                        isBulkOrderFn={isBulkOrderFn}
                        cartResponse={cartResponse}
                        goToMenu={goToMenu}
                        isPopCart={isPopCart}
                        isIrctcCartSession={isIrctcCartSession}
                    />
                </CartGroupContainer>
            </CartGroupContainerWrapper>
        );
    }, [
        cartResponse,
        menuCallout?.info,
        menuItemsCalloutAnalytics,
        itemListHeader,
        isBulkOrderFn,
        isValueMeal,
        itemImageOnCartXpFlagExcludingBulkOrder,
        isIrctcCartSession,
        disableRedirectionToMenu,
        localCart?.orderComment,
        isCroutonVisible,
        goToMenu,
        isPopCart,
    ]);

    const crossSellWidget = useCallback(() => {
        if (isEmpty(cartResponse) || isCartBoltEnabled) {
            return null;
        }

        return (
            <CrossSellM2ItemRenderer
                onImpressionCrossSellM2Item={onImpressionCrossSellM2Item}
                onImpressionAddItemCarousel={onImpressionAddItemCarousel}
                onClickAddItemCarousel={onClickAddItemCarousel}
                onClickItemDetailsCarousel={onClickItemDetailsCarousel}
                onImpressionCarouselItemDetailsPopup={onImpressionCarouselItemDetailsPopup}
                onImpressionCartItemDetails={onImpressionCartItemDetails}
                onImpressionMagicItemsUnlockPopup={onImpressionMagicItemsUnlockPopup}
                onClickDismissMagicItemsUnlockPopup={onClickDismissMagicItemsUnlockPopup}
                onClickExploreMenuMagicItems={onClickExploreMenuMagicItems}
                goToMenu={goToMenu}
            />
        );
    }, [
        cartResponse,
        isCartBoltEnabled,
        onImpressionCrossSellM2Item,
        onImpressionAddItemCarousel,
        onClickAddItemCarousel,
        onClickItemDetailsCarousel,
        onImpressionCarouselItemDetailsPopup,
        onImpressionCartItemDetails,
        onImpressionMagicItemsUnlockPopup,
        onClickDismissMagicItemsUnlockPopup,
        onClickExploreMenuMagicItems,
        goToMenu,
    ]);

    const snaccFreebieWidget = useCallback(() => {
        if (isEmpty(cartResponse) || !shouldShowFreebieWidget(cartResponse) || !isPopCart || isIrctcCartSession) {
            return null;
        }
        const widgetData = getFreebieWidgetData(cartResponse);
        return (
            <FreebieWidgetRenderer
                widgetData={widgetData as FreebieWidgetRenderingDetails}
                cartResponse={cartResponse}
            />
        );
    }, [cartResponse, isPopCart, isIrctcCartSession]);

    const stealDealWidget = useCallback(() => {
        if (isEmpty(cartResponse) || !shouldShowStealDealWidget(cartResponse) || !isPopCart) {
            return null;
        }

        // Extract steal deal widget from rendering details
        const stealDealWidgetData = getStealDealWidgetData(cartResponse);

        if (!stealDealWidgetData) {
            return null;
        }

        return (
            <StealDealWidgetRenderer
                widgetData={stealDealWidgetData as StealDealWidgetRenderingDetails}
                cartResponse={cartResponse}
            />
        );
    }, [cartResponse, isPopCart]);

    const renderWidget = useCallback(
        (widgetSection: RenderingDetails) => {
            const widgetType = widgetSection?.type?.toUpperCase();
            switch (widgetType) {
                case VALID_RENDERING_DETAILS_TYPE.MENU_ITEMS_ADDED_TO_CART:
                    return menuItemsWidget();
                case VALID_RENDERING_DETAILS_TYPE.CART_CROSS_SELL_ITEMS_SUGGESTION:
                    return crossSellWidget();
                case VALID_RENDERING_DETAILS_TYPE.SNACC_FREEBIE_WIDGET:
                    return snaccFreebieWidget();
                case VALID_RENDERING_DETAILS_TYPE.STEAL_DEAL_WIDGET:
                    return stealDealWidget();
                default:
                    return null;
            }
        },
        [crossSellWidget, menuItemsWidget, snaccFreebieWidget, stealDealWidget],
    );

    useEffectOnce(() => {
        const widgetSequence = JSON.stringify(
            widgetSections.reduce(
                (sequence, section, position) => {
                    sequence[section.type] = position + 1;
                    return sequence;
                },
                {} as Record<string, number>,
            ),
        );

        impressionCartWidgetRanking({
            impressionEvent: impressionEvent,
            widgetSequence,
            cartId: cartResponse?.data?.cartId || "",
        });
    });

    return <>{widgetSections.map(renderWidget)}</>;
}

export default memo(CartMenuItemsAndSuggestions);
